import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import { isEmpty } from 'lodash';
import { connect } from 'react-redux';
import classnames from 'classnames';

import type { IMonth } from '@lt/components/components/MonthSegments/Month';
import { MonthSegments } from '@lt/components';
import {
  DESKTOP_MAP_TOP_OFFSET,
  HEADER_OFFSET_SEARCH,
} from 'src/constants/header';
import { useInView } from 'react-intersection-observer';
import { getHeaderBannerOffset } from 'src/store/headerBanner/selectors';
import styles from './headerContainer.module.css';

// Components:
// import { pushExploreLinkClicked } from 'src/routes/SeoApp/utils/gtmEvents';
import { Breadcrumbs } from './components/Breadcrumbs';

// Types:
import type { ModifierStyles } from '../../domain/state/modifier/types';

// Selectors:
import { getMetadata } from '../../domain/state/metadata/selectors';
import { modifierStylesSelector } from '../../domain/state/modifier/selectors';
import { getLinkedMonths } from '../../domain/state/linked/selectors';

// Context:
import { MobileContext } from '../../domain/context/MobileContext';

const mapStateToProps = (state) => ({
  metadata: getMetadata(state),
  modifierStyles: modifierStylesSelector(state),
  linkedMonths: getLinkedMonths(state),
  headerBannerOffset: getHeaderBannerOffset(state),
});

interface HeaderContainerProps {
  metadata: {
    breadcrumbs: Array<{
      text: string;
      link: string;
    }>;
    h1: string;
    short_description: string;
  };
  modifierStyles: ModifierStyles;
  linkedMonths: IMonth[];

  className?: string;
  setVisibleHeight: (height: number) => void;
  headerBannerOffset: number;
}

type CustomCSSProperties = {
  '--textColor'?: string;
  '--boldTextColor'?: string;
  '--linkColor'?: string;
  '--highlitedBlockColor'?: string;
};

type StyleProps = React.CSSProperties & CustomCSSProperties;

export const HeaderContainer = connect(mapStateToProps)(
  ({
    metadata: { breadcrumbs, h1, short_description },
    className = '',
    modifierStyles,
    linkedMonths,
    setVisibleHeight,
    headerBannerOffset,
  }: HeaderContainerProps) => {
    const isMobile = useContext(MobileContext);

    // начальная позиция без учета баннера;
    const initialTop = HEADER_OFFSET_SEARCH;

    const headerRef = useRef<HTMLDivElement | null>(null);

    const {
      ref: inViewRef,
      inView,
      entry,
    } = useInView({
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
      trackVisibility: true,
      delay: 100,
    });

    const setRefs = useCallback(
      (node) => {
        headerRef.current = node;
        inViewRef(node);
      },
      [inViewRef],
    );

    const [currentTop, setCurrentTop] = useState(initialTop);

    const handleScroll = () => {
      if (headerRef.current) {
        const { top } = headerRef.current.getBoundingClientRect();
        setCurrentTop(top);
      }
    };

    useEffect(() => {
      window.addEventListener('scroll', handleScroll);

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }, []);

    useEffect(() => {
      if (!inView) {
        setVisibleHeight(-DESKTOP_MAP_TOP_OFFSET);
      } else if (inView && entry) {
        const { height } = entry.intersectionRect;

        //  смещение блока
        const offsetDifference = initialTop - currentTop;

        if (offsetDifference >= height) {
          setVisibleHeight(-DESKTOP_MAP_TOP_OFFSET);
        } else if (
          inView &&
          offsetDifference < height &&
          offsetDifference > 0
        ) {
          setVisibleHeight(height - offsetDifference);
        } else if (
          inView &&
          offsetDifference < 0 &&
          offsetDifference > -headerBannerOffset
        ) {
          setVisibleHeight(height + offsetDifference);
        } else {
          setVisibleHeight(height);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView, setVisibleHeight, currentTop, entry, initialTop]);

    return (
      <section
        ref={setRefs}
        className={classnames(styles.container, className, {
          [styles.containerMobile]: isMobile,
          [styles.backgroundColor]: modifierStyles?.backgroundColor,
          [styles.textColor]: modifierStyles?.textColor,
          [styles.boldTextColor]: modifierStyles?.boldTextColor,
          [styles.linkColor]: modifierStyles?.linkColor,
          [styles.highlitedBlockColor]: modifierStyles?.highlitedBlockColor,
        })}
        style={
          {
            backgroundColor: modifierStyles?.backgroundColor,
            '--textColor': modifierStyles?.textColor,
            '--boldTextColor': modifierStyles?.boldTextColor,
            '--linkColor': modifierStyles?.linkColor,
            '--highlitedBlockColor': modifierStyles?.highlitedBlockColor,
          } as StyleProps
        }
      >
        {!isEmpty(breadcrumbs) && <Breadcrumbs breadcrumbs={breadcrumbs} />}
        <h1
          className={classnames(styles.title, {
            [styles.titleMobile]: isMobile,
          })}
        >
          {h1}
        </h1>

        <p
          className={classnames(styles.description, {
            [styles.descriptionMobile]: isMobile,
          })}
        >
          {short_description}
        </p>

        {linkedMonths && (
          <MonthSegments
            // onClick={() => () => {
            //   pushExploreLinkClicked({
            //     custom_params: {
            //       type: 'month',
            //     },
            //   });
            // }}
            months={linkedMonths}
            className={styles.monthSegments}
          />
        )}
      </section>
    );
  },
);
